import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { MdKeyboardArrowUp, MdKeyboardArrowDown } from 'react-icons/lib/md';
import { type, trim, uniqBy, last, path, defaultTo, isNil, filter, contains } from 'ramda';
import cn from 'classnames';

import Card from '../Card/index';
import Avatar from '../Avatar/index';
import { isNilOrEmpty } from '../../util/redux';
import magic from '../../util/magic';

class UserCard extends Component {
  static propTypes = {
    user: PropTypes.object,
    showMembership: PropTypes.bool,
    isSearch: PropTypes.bool,
    onContact: PropTypes.func,
  };

  state = {
    expanded: false,
  };

  renderUserInfo = (label, data, { compact, centered, allowEmpty, fullWidth } = {}) => {
    if (type(data) == 'String') {
      data = trim(defaultTo('', data));
    }
    if (isNilOrEmpty(data) && !allowEmpty) {
      data = 'N/A';
    }

    const wrapper = fullWidth ? 'col-md-12' : 'col-md-6';

    if (compact) {
      return (
        <div key={label} className={cn(wrapper, { 'd-flex align-items-center': centered })}>
          <span className="font-weight-bold">{label}: </span> {data}
        </div>
      );
    } else {
      return (
        <div key={label} className={wrapper}>
          <div className="font-weight-bold dark-blue-heading">
            {label}
            {isNilOrEmpty(data) ? '' : ':'}
          </div>
          <p>{data}</p>
        </div>
      );
    }
  };

  render() {
    const { user, showMembership, isSearch, onContact } = this.props;
    const { expanded } = this.state;
    const memberships = defaultTo([], user.userMemberships);
    const currentMembership = defaultTo({}, last(memberships));
    const addressFields = [path(['address', 'city'], user), path(['address', 'state'], user)].filter((a) => a);
    const location = [
      !isSearch ? path(['address', 'addressLine1'], user) : null,
      addressFields.join(', '),
      !isSearch ? path(['address', 'zip'], user) : null,
    ].filter((a) => a);
    const year = new Date().getFullYear();
    const militaryInstallation = path(['militaryInstallation', 'name'], user);
    let activeCertifications = [];
    if (!isSearch)
      activeCertifications = filter(
        (cert) =>
          contains(cert.status, [
            magic.certificateGoodStanding,
            magic.certificateLapsedCEUs,
            magic.certificateLapsedPayment,
          ]),
        activeCertifications,
      );
    activeCertifications = uniqBy((cert) => cert.name, user.userCertifications);

    return (
      <div className="d-flex mb-5">
        <Avatar large square src={user.avatar} className="highIndex" />
        <Card className="flex-grow-1">
          <Card.Body>
            <h2 className="blue-heading">
              {user.firstName} {user.lastName}
            </h2>
            <div className="row">
              {this.renderUserInfo('Certifications', activeCertifications.map((cert) => cert.name).join(', '), {
                compact: true,
              })}
              {showMembership && this.renderUserInfo('MentorConnect', user.mentorConnect, { compact: true })}
              {this.renderUserInfo('Web', user.website, { compact: true })}
              {!isSearch && this.renderUserInfo('Email', user.email, { compact: true })}
              {isSearch &&
                this.renderUserInfo(
                  'Contact',
                  <button className="btn btn-link btn-sm" onClick={onContact}>
                    Send a message
                  </button>,
                  { compact: true, centered: true },
                )}
              {this.renderUserInfo('Location', location.join(' '), { compact: true })}
              {showMembership && currentMembership.year >= year
                ? this.renderUserInfo('Membership', path(['membership', 'name'], currentMembership), {
                    compact: true,
                  })
                : ''}
              {!isSearch && this.renderUserInfo('Home Phone', user.homePhone, { compact: true })}
              {this.renderUserInfo('Phone', user.workPhone, { compact: true })}
              {this.renderUserInfo(
                'Offers Virtual Counseling',
                user.offerVirtualCounseling != null ? (user.offerVirtualCounseling ? 'Yes' : 'No') : null,
                { compact: true },
              )}
              {this.renderUserInfo(
                'Offers Local Counseling',
                user.offerLocalCounseling != null ? (user.offerLocalCounseling ? 'Yes' : 'No') : null,
                { compact: true },
              )}
              {this.renderUserInfo(
                'Offers Group Workshops',
                user.offerGroupWorkshops != null ? (user.offerGroupWorkshops ? 'Yes' : 'No') : null,
                { compact: true },
              )}
              {showMembership
                ? this.renderUserInfo('Registered For Symposium', user.registeredForSymposium ? 'Yes' : 'No', {
                    compact: true,
                  })
                : ''}
              {this.renderUserInfo('Interested in being contacted', user.provideCounselingServices ? 'Yes' : 'No', {
                compact: true,
              })}
            </div>
            {(user.organization ||
              user.jobTitle ||
              user.yearsOfService ||
              user.areasOfExpertise.length > 0 ||
              user.provideCounselingServices != null ||
              user.offerVirtualCounseling != null ||
              user.offerLocalCounseling != null ||
              user.offerGroupWorkshops != null) && (
              <Fragment>
                <hr />
                <div className="row">
                  {this.renderUserInfo('Organization', user.organization)}
                  {this.renderUserInfo('Job Title', user.jobTitle)}
                  {this.renderUserInfo('Experience', user.yearsOfService ? `${user.yearsOfService} Years` : null)}
                  {militaryInstallation && this.renderUserInfo('Military Installation', militaryInstallation)}
                  {this.renderUserInfo(
                    'Areas Of Expertise',
                    user.areasOfExpertise.map((area) => area.name).join(', '),
                    {
                      fullWidth: true,
                    },
                  )}
                </div>
              </Fragment>
            )}
            {(user.workAreaCategories.length > 0 || user.areasOfWorkOther) && showMembership && (
              <Fragment>
                <hr />
                <div className="row">
                  <h3 className="col-12 dark-blue-heading">Professional areas of work</h3>
                  {user.workAreaCategories.map((workAreaCategory) => {
                    const workAreas = user.workAreas.filter((area) => area.workAreaCategoryId == workAreaCategory.id);
                    return this.renderUserInfo(workAreaCategory.name, workAreas.map((area) => area.name).join(', '), {
                      allowEmpty: true,
                    });
                  })}
                  {user.areasOfWorkOther && this.renderUserInfo('Other', user.areasOfWorkOther)}
                </div>
              </Fragment>
            )}
            {user.bio && (
              <Fragment>
                <hr />
                <div className="row">
                  <div className="col-md-12">
                    <div
                      className="clickable font-weight-bold dark-blue-heading py-2"
                      onClick={() => this.setState({ expanded: !expanded })}
                    >
                      Biography:
                      {expanded ? (
                        <MdKeyboardArrowUp className="float-right" size="2em" />
                      ) : (
                        <MdKeyboardArrowDown className="float-right" size="2em" />
                      )}
                    </div>
                    {expanded && <p className="whiteSpaceWrap">{user.bio}</p>}
                  </div>
                </div>
              </Fragment>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  }
}

export default UserCard;
